import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useReplayClock from './useReplayClock';
import RunCanvas from './RunCanvas';
import ReceiptCard from './ReceiptCard';
import { ReviewCard } from '../ModuleStages';
import EcosystemFrame from '../three/EcosystemFrame';
import { deriveCommand } from '../three/commandState';
import commandDemo, { DOWN_REPORTS, RESTART_SEC, RESTART_SOURCE } from '../../data/commandDemo';
import './landing.css';

/* "One invoice, four screens." The homepage spine — Design · Gate · Commit ·
   Operate — as one replayed run across four real screens, on one clock:
   the nQube canvas (the for-each reading three invoices), the Human Review
   item holding the one over policy, the commit record the runtime writes
   when it posts, and the Command View of the instance that ran it. Hover
   or focus a step to scrub the clock to that step. Frozen at the final
   frame when motion is not wanted (reduced motion) or not seen
   (prerender), so the finished run is what crawlers and the no-motion
   page read. The run is the demo org's "Invoice posting — gated over $10k"
   ([[homepage-film]] · media/invoice-film). */

export const TIMELINE = {
  length: 30,
  hold: 3,
  phases: [
    { id: 'design', from: 0, to: 10 },
    { id: 'gate', from: 10, to: 15 },
    { id: 'commit', from: 15, to: 20 },
    { id: 'operate', from: 20, to: 30 },
  ],
  review: { arrives: 10.5, decided: 13.5 },
  commit: { at: 16.5 },
  fleet: { report1: 21, report2: 22.2, restarted: 24.6 },
};

const phaseAt = (t) => TIMELINE.phases.find((p) => t >= p.from && t < p.to) || TIMELINE.phases[TIMELINE.phases.length - 1];

export function GatePanel({ t }) {
  const { arrives, decided } = TIMELINE.review;
  const here = t >= arrives;
  const ok = t >= decided;
  return (
    <div className="gp">
      <div className="gp-queue">
        <span className="gp-queue-title">Human Review</span>
        <span className={`gp-queue-count ${here && !ok ? 'is-hot' : ''}`}>{here && !ok ? '1 urgent' : ok ? '0 waiting' : 'queue empty'}</span>
      </div>
      {here ? (
        <ReviewCard
          decided={ok}
          title="A person approves"
          meta="INV-20841 · $12,480 · over $10,000 · from Over $10k? → pass"
          preview={'vendor: Northwind Supply\ninvoice: INV-20841\namount: 12480.00\nterms: net 30\nHOLD — over policy ($10,000)'}
          reject="Reject"
          extra="Return"
          note={ok ? 'Approved by the controller. A yes here does not hand the model a pen — it lets the runtime post.' : 'Approve, edit the fields, or reject. Nothing posts until you do.'}
        />
      ) : (
        <div className="gp-wait"><span>The keyword rule is reading the batch for <code>HOLD</code>. An invoice over policy lands here, for a person. The ones under it post on their own.</span></div>
      )}
      <ul className="gp-rules">
        <li className="is-ok"><i /><span>A person decides — <code>control.review</code></span></li>
        <li className="is-ok"><i /><span>A keyword rule decides — <code>control.filter</code> · <code>HOLD</code></span></li>
        <li className="is-no"><i /><span><s>The model says yes</s> — not a gate</span></li>
      </ul>
    </div>
  );
}

function OperatePanel({ t, active }) {
  const { report1, report2, restarted } = TIMELINE.fleet;
  const phase = t < report1 ? 'calm' : t < report2 ? 'down' : t < restarted ? 'restarting' : 'healed';
  const data = useMemo(() => commandDemo(phase), [phase]);
  const reports = t >= report2 ? DOWN_REPORTS : t >= report1 ? 1 : 0;
  return (
    <div className="op">
      <div className="op-bar">
        <strong>Command View · northline</strong>
        <span className={`op-level is-${phase}`}>{phase === 'calm' ? 'all quiet' : phase === 'down' ? `api reported down ${reports}×` : phase === 'restarting' ? 'fleet restarting api' : `api up · ${RESTART_SEC.toFixed(1)} s`}</span>
      </div>
      {/* Drag to orbit here; "enter room ↗" opens the same picture as a full room on the demo feed. */}
      <EcosystemFrame data={data} derive={deriveCommand} scene="command" compact fill caption={false} ticker={false} className={active ? '' : 'is-paused'} roomHref="/demo/room" />
      <ol className="op-remed">
        <li className={t >= report1 ? 'is-on' : ''}><i /><span>reported down</span></li>
        <li className={t >= report2 ? 'is-on' : ''}><i /><span>reported down · {DOWN_REPORTS}×</span></li>
        <li className={t >= report2 ? 'is-on is-act' : ''}><i /><span>fleet restarts the service</span></li>
        <li className={t >= restarted ? 'is-on is-ok' : ''}><i /><span>healthy · <strong>{RESTART_SEC.toFixed(1)} s</strong> · on the record</span></li>
      </ol>
      <small className="op-src">{RESTART_SOURCE}</small>
    </div>
  );
}

export default function SpineRun({ steps }) {
  const root = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !root.current) { setActive(true); return undefined; }
    const io = new IntersectionObserver((es) => es.forEach((e) => setActive(e.isIntersecting)), { rootMargin: '80px 0px' });
    io.observe(root.current);
    return () => io.disconnect();
  }, []);
  const { t, seek, frozen, length } = useReplayClock({ length: TIMELINE.length, hold: TIMELINE.hold, active });
  const current = phaseAt(t).id;
  const panels = {
    design: <RunCanvas t={t} />,
    gate: <GatePanel t={t} />,
    commit: <ReceiptCard t={t} at={TIMELINE.commit.at} />,
    operate: <OperatePanel t={t} active={active && !frozen} />,
  };
  return (
    <div className="spine" ref={root} data-phase={current}>
      <div className="spine-bar" aria-hidden="true">
        {TIMELINE.phases.map((p, i) => (
          <button key={p.id} type="button" className={`spine-seg ${p.id === current ? 'is-current' : t >= p.to ? 'is-done' : ''}`} onClick={() => seek(p.from)} tabIndex={-1}>
            <span>{steps[i].k}</span>
          </button>
        ))}
        {!frozen && <i className="spine-head" style={{ left: `${(t / length) * 100}%` }} />}
      </div>
      <ol className="cs-land-steps spine-steps">
        {steps.map((st, i) => {
          const p = TIMELINE.phases[i];
          return (
            <li
              key={st.k}
              className={`spine-step ${p.id === current ? 'is-current' : ''}`}
              onMouseEnter={() => seek(p.from)}
              onFocus={() => seek(p.from)}
            >
              <div className="spine-art">{panels[p.id]}</div>
              <span className="cs-land-step-n">{st.n}</span>
              <h3>{st.k}</h3>
              <p>{st.what}</p>
              <Link to={st.to} className="cs-land-proof-link">{st.cta} →</Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
